import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import BuildIcon from '@mui/icons-material/Build'
import React, { useState } from 'react'
import ConfigForm from './ConfigForm'
import ConfigOutput from './ConfigOutput'
import type { StorefrontPackage, RenderedConfig, SchemaDefinition } from '../../types'

interface Props {
  packages: StorefrontPackage[]
  vendorSlug: string
  entrySlug: string
}

export default function ConfigGenerator({ packages, vendorSlug, entrySlug }: Props) {
  const [selectedPkgId, setSelectedPkgId] = useState<number>(packages[0]?.id || 0)
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [result, setResult] = useState<RenderedConfig | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const selectedPkg = packages.find(p => p.id === selectedPkgId)

  const handlePackageChange = (pkgId: number) => {
    setSelectedPkgId(pkgId)
    setValues({})
    setResult(null)
    setError(null)
  }

  const handleFieldChange = (name: string, value: unknown) => {
    setValues(prev => ({ ...prev, [name]: value }))
  }

  const handleGenerate = async () => {
    setLoading(true)
    setError(null)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const response = await fetch(`/apps/${vendorSlug}/${entrySlug}/render_config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ package_id: selectedPkgId, args: values }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || 'Failed to generate config')
        return
      }

      setResult(data.config)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configuration Generator
        </Typography>

        {packages.length > 1 && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Deployment Package</InputLabel>
            <Select
              value={selectedPkgId}
              label="Deployment Package"
              onChange={(e) => handlePackageChange(e.target.value as number)}
            >
              {packages.map((pkg) => (
                <MenuItem key={pkg.id} value={pkg.id}>
                  {pkg.name} ({pkg.deploymentTypeLabel})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedPkg?.schema && (
          <ConfigForm
            schema={selectedPkg.schema as SchemaDefinition}
            values={values}
            onChange={handleFieldChange}
          />
        )}

        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? 'Generating...' : 'Generate Config'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        )}

        {result && (
          <Box sx={{ mt: 3 }}>
            <ConfigOutput config={result} />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
